import { uniq } from "@-/com.npmjs.array-utils-ts";

import type { CardContentKind } from "#card-content";

/**
 * The actor overlay table backing each card-content kind.
 *
 * Card reads invalidate on table-change versions, and the table lists were previously spelled
 * out by hand in four separate `as const` arrays (`STUDY_CARD_DATA_LIST_TABLES`,
 * `NEXT_DUE_USER_CARD_DATA_TABLES`, `USER_CARD_APP_CARD_TABLES`, `DUE_ROWS_COUNT_TABLES`).
 * Deriving them from one map means a new kind cannot be added to some lists and forgotten in
 * others — a stale-cache class of bug that is invisible until a card fails to appear.
 */
export const CARD_CONTENT_OVERLAY_TABLE_BY_KIND = {
  "grapheme-meaning": "user_grapheme_meaning_card",
  "grapheme-pronunciation": "user_grapheme_pronunciation_card",
  quiz: "user_quiz_card",
  // All three word kinds share ONE overlay table, discriminated by its `kind` column. This is the
  // first time two kinds map to the same table, which is why the list below must dedupe.
  "word-characters-in-context": "user_word_sentence_card",
  "word-characters-in-sentence": "user_word_sentence_card",
  "word-pronunciation-in-sentence": "user_word_sentence_card",
} as const satisfies Record<CardContentKind, string>;

/**
 * Every actor overlay table a card's identity can live in.
 *
 * Deduped: several kinds share one table, and consumers feed this straight to `tableNameMatcher`
 * for cache invalidation, where a repeated name is wasted work on every change event.
 */
export const CARD_CONTENT_OVERLAY_TABLES = uniq(
  Object.values(CARD_CONTENT_OVERLAY_TABLE_BY_KIND),
) as readonly (typeof CARD_CONTENT_OVERLAY_TABLE_BY_KIND)[CardContentKind][];
